import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getCookieConsent } from '../helpers/cookieConsent';

// Unset in local dev unless a .env defines it (see .env.example) -- CRA
// inlines REACT_APP_* at build time, so production deploys must have this
// set wherever `npm run build` actually runs, not just in a local .env.
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Must match the event name src/helpers/cookieConsent.js dispatches on
// window from saveCookieConsent()/resetCookieConsent() -- not imported as a
// shared constant to avoid touching that file for this task.
const CONSENT_CHANGED_EVENT = 'pixelcraft:cookie-consent-changed';

// window.__PRERENDERING__ is set only by scripts/build/prerender.js's
// headless-Chrome pass (see src/components/typedText/TypedText.js for the
// same guard elsewhere). Without this, every `npm run build` would fire a
// real gtag.js load and page_view hits for every route, in Google's real
// account, from a machine that never had a real visitor.
const isPrerendering = () => typeof window !== 'undefined' && window.__PRERENDERING__ === true;

let initialized = false;

/**
 * Loads gtag.js and wires up Google Consent Mode (v2). Call once, from
 * App.js on mount -- idempotent (safe under React.StrictMode's dev-only
 * double-invoke, see src/index.js).
 *
 * Architecture: "advanced" Consent Mode -- gtag.js loads unconditionally,
 * but `gtag('consent','default',...)` is pushed to the dataLayer *before*
 * the script tag and before any config/event call, so GA never sets
 * analytics cookies (_ga / _ga_*) until analytics_storage is explicitly
 * granted (Google's recommended default over fully blocking the script --
 * required for GA4 + Consent Mode v2 for EEA traffic since March 2024).
 * ad_storage/ad_user_data/ad_personalization stay denied permanently --
 * this site runs no ads/remarketing, nothing here ever grants them; they're
 * only declared because GA4 expects all four and Google's docs recommend
 * setting them explicitly rather than leaving them unset.
 *
 * send_page_view: false on the one `config` call below is deliberate --
 * usePageViewTracking() below is the single source of every page_view
 * (including the first one), so gtag's own automatic page_view must stay
 * off or the first route would fire two.
 */
export function initAnalytics() {
  if (initialized || isPrerendering() || !GA_MEASUREMENT_ID) return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  // The standard gtag.js shim: defines window.gtag synchronously so every
  // call below queues into dataLayer immediately, regardless of whether
  // the async script tag has finished loading yet.
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  // Reflects a *returning* visitor's already-stored choice as the very
  // first consent state (no unnecessary denied->granted flash) -- a
  // first-time visitor has no stored consent, so every category is denied,
  // exactly as required before any decision has been made.
  const stored = getCookieConsent();
  window.gtag('consent', 'default', {
    analytics_storage: stored?.analytics ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });

  window.addEventListener(CONSENT_CHANGED_EVENT, (event) => {
    window.gtag('consent', 'update', {
      analytics_storage: event.detail?.analytics ? 'granted' : 'denied',
    });
  });
}

export function trackPageView(path, title) {
  if (!initialized || isPrerendering() || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
  });
}

// Fires exactly one page_view per route view -- the initial load included,
// since useEffect below runs on mount too, so gtag's own automatic
// page_view (disabled above) is never needed.
//
// Timing is the hard part: App.js's <AnimatePresence mode="wait"> keeps
// the *previous* route's component (and its own stale Helmet title)
// mounted for its full 500ms exit transition before the new route mounts
// at all -- location.pathname (this hook's own dependency) already updates
// the instant navigation starts, well before that. Waiting for
// ".page-loader" to disappear (App.js's usePrerenderStatusSignal's own
// fix for the *initial*-load version of this problem) doesn't help here:
// there's no new loader to wait for yet, since the new route hasn't begun
// mounting. So: wait out that known, fixed 500ms exit-animation window
// first (600ms for margin), then poll until document.title stops changing
// for two consecutive frames -- covers whatever's left (a lazy route
// chunk that hadn't been fetched yet, react-helmet-async's own
// rAF-scheduled commit), capped so a slow/stuck route still gets a late
// page_view instead of none. A flat ~600ms floor on every page_view
// (including the first, which has no exit animation to wait for at all)
// is a deliberate, acceptable simplicity trade-off -- analytics timing
// doesn't need to be tight, and a single uniform wait is far easier to
// reason about than special-casing "is this the first load."
export function usePageViewTracking() {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    let rafId;

    const settleTimer = setTimeout(() => {
      if (cancelled) return;
      let lastTitle = null;
      let stableChecks = 0;
      let frames = 0;
      const MAX_FRAMES = 180; // ~3s ceiling on top of the 600ms floor below

      const poll = () => {
        if (cancelled) return;
        frames += 1;
        const title = document.title;
        if (title === lastTitle) {
          stableChecks += 1;
        } else {
          lastTitle = title;
          stableChecks = 1;
        }
        if (stableChecks >= 2 || frames >= MAX_FRAMES) {
          trackPageView(location.pathname + location.search, title);
          return;
        }
        rafId = requestAnimationFrame(poll);
      };
      rafId = requestAnimationFrame(poll);
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(settleTimer);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [location.pathname, location.search]);
}
